// src/dashboard.js
import React, { useState, useEffect, useRef } from 'react';
import { Search, Users, BarChart3, LayoutDashboard, Grid, List, Edit2, X, Check, Trash2, Download, Plus, Settings, Shield, Clock, Menu, ChevronLeft } from 'lucide-react';
import { useOktaAuth } from '@okta/okta-react';
import './dashboard.css';
import UserProfile from './UserProfile';
import ManagerTab from './ManagerTab';
import AdminTab from './AdminTab';
import RBACManagement from './RBACManagement';
import AccessRequests from './AccessRequests';
import TeamsManagement from './TeamsManagement';
import ResourceManagement from './ResourceManagement';
import FunctionalRoleManagement from './FunctionalRoleManagement';
import BulkUploadManagement from './BulkUploadManagement';
import SearchableDropdown from './SearchableDropdown';

const PAGE_SIZE = 500;

// Map status text to CSS class
const getStatusClass = (status) => {
  switch (status) {
    case 'Active':   return 'status-badge status-active';
    case 'Offboarding': return 'status-badge status-inactive';
    case 'Onboarding':     return 'status-badge status-away';
    default:         return 'status-badge';
  }
};

const EditModal = ({ member, isOpen, onClose, onSave, dropdownOptions }) => {
  const [formData, setFormData] = useState({
    role: member?.role || '',
    manager: member?.manager || '',
    department: member?.team || '',
    status: member?.status || 'Active'
  });
  const [loading, setLoading] = useState(false);
  const [teams, setTeams] = useState([]);
  const [roles, setRoles] = useState([]);

  useEffect(() => {
    if (member) {
      setFormData({
        role: member.role || '',
        manager: member.manager || '',
        department: member.team || '',
        status: member.status || 'Active'
      });
    }
  }, [member]);

  useEffect(() => {
    if (isOpen) {
      fetchTeamsAndRoles();
    }
  }, [isOpen]);

  const fetchTeamsAndRoles = async () => {
    try {
      const [teamsResponse, rolesResponse] = await Promise.all([
        fetch('/api/teams'),
        fetch('/api/roles')
      ]);

      if (teamsResponse.ok && rolesResponse.ok) {
        const teamsData = await teamsResponse.json();
        const rolesData = await rolesResponse.json();
        setTeams(teamsData);
        setRoles(rolesData);
      }
    } catch (error) {
      console.error('Error fetching teams and roles:', error);
    }
  };

  // Filter roles based on selected team
  const filteredRoles = formData.department 
    ? roles.filter(role => {
        const selectedTeam = teams.find(team => team.Title === formData.department);
        return selectedTeam && String(role.TeamID) === String(selectedTeam.TeamID);
      })
    : roles;

  // Auto-populate manager when team is selected
  const handleTeamChange = (e) => {
    const selectedTeamTitle = e.target.value;
    const selectedTeam = teams.find(team => team.Title === selectedTeamTitle);
    
    setFormData(prev => ({
      ...prev,
      department: selectedTeamTitle,
      manager: selectedTeam ? selectedTeam.Manager : '',
      role: '' // Reset role when team changes
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const backendData = {
        role: formData.role,
        manager: formData.manager,
        department: formData.department
      };

      // Send only role, manager, and department to backend **NEEDS TO UPDATE IN FINAL PRODUCT**
      const response = await fetch(`/api/employees/${member.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(backendData),
      });

      if (response.ok) {
        onSave(member.id, formData);
        onClose();
      } else {
        console.error('Failed to update employee');
      }
    } catch (error) {
      console.error('Error updating employee:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Edit Employee: {member?.name}</h3>
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group">
            <label>Team</label>
            <select
              value={formData.department}
              onChange={handleTeamChange}
              required
            >
              <option value="">Select Team</option>
              {teams.map(team => (
                <option key={team.TeamID} value={team.Title}>{team.Title}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Manager</label>
            <input
              type="text"
              value={formData.manager}
              placeholder="Manager will be auto-populated when team is selected"
              readOnly
              style={{
                backgroundColor: '#f9fafb',
                color: '#6b7280',
                cursor: 'not-allowed'
              }}
            />
          </div>

          <div className="form-group">
            <label>Role</label>
            <select
              value={formData.role}
              onChange={e => setFormData(prev => ({ ...prev, role: e.target.value }))}
              required
              disabled={!formData.department}
            >
              <option value="">
                {formData.department ? 'Select Role' : 'Select Team first'}
              </option>
              {filteredRoles.map(role => (
                <option key={role.RoleID} value={role.Title}>{role.Title}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Status</label>
            <select
              value={formData.status}
              onChange={e => setFormData(prev => ({ ...prev, status: e.target.value }))}
              required
            >
              {dropdownOptions.statuses?.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn-cancel">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="btn-save">
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AddEmployeeModal = ({ isOpen, onClose, onAdd, dropdownOptions }) => {
  const [formData, setFormData] = useState({
    name: '',
    roles: [],
    manager: '',
    department: ''
  });
  const [loading, setLoading] = useState(false);
  const [teams, setTeams] = useState([]);
  const [roles, setRoles] = useState([]);
  const [employeeNames, setEmployeeNames] = useState([]);

  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: '',
        roles: [],
        manager: '',
        department: ''
      });
      fetchTeamsAndRoles();
      fetchEmployeeNames();
    }
  }, [isOpen]);

  const fetchTeamsAndRoles = async () => {
    try {
      const [teamsResponse, rolesResponse] = await Promise.all([
        fetch('/api/teams'),
        fetch('/api/roles')
      ]);

      if (teamsResponse.ok && rolesResponse.ok) {
        const teamsData = await teamsResponse.json();
        const rolesData = await rolesResponse.json();
        setTeams(teamsData);
        setRoles(rolesData);
      }
    } catch (error) {
      console.error('Error fetching teams and roles:', error);
    }
  };

  const fetchEmployeeNames = async () => {
    try {
      const response = await fetch('/api/employees?limit=1000');
      if (response.ok) {
        const employeesData = await response.json();
        const names = employeesData.map(emp => emp.name).sort();
        setEmployeeNames(names);
      }
    } catch (error) {
      console.error('Error fetching employee names:', error);
    }
  };

  // Filter roles based on selected team
  const filteredRoles = formData.department 
    ? roles.filter(role => {
        const selectedTeam = teams.find(team => team.Title === formData.department);
        return selectedTeam && String(role.TeamID) === String(selectedTeam.TeamID);
      })
    : roles;

  // Auto-populate manager when team is selected
  const handleTeamChange = (e) => {
    const selectedTeamTitle = e.target.value;
    const selectedTeam = teams.find(team => team.Title === selectedTeamTitle);
    
    setFormData(prev => ({
      ...prev,
      department: selectedTeamTitle,
      manager: selectedTeam ? selectedTeam.Manager : '',
      roles: [] // Reset roles when team changes
    }));
  };

  const handleRoleAdd = (roleTitle) => {
    if (roleTitle && !formData.roles.includes(roleTitle)) {
      setFormData(prev => ({
        ...prev,
        roles: [...prev.roles, roleTitle]
      }));
    }
  };

  const handleRoleRemove = (roleToRemove) => {
    setFormData(prev => ({
      ...prev,
      roles: prev.roles.filter(role => role !== roleToRemove)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.manager || !formData.department) {
      alert('Name, team, and manager are required');
      return;
    }

    if (formData.roles.length === 0) {
      alert('At least one role is required');
      return;
    }

    setLoading(true);
    
    try {
      const payload = {
        name: formData.name,
        roles: formData.roles,
        role: formData.roles[0], // Primary role
        manager: formData.manager,
        department: formData.department
      };

      const response = await fetch('/api/employees', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const result = await response.json();
        onAdd(result.employee);
        onClose();
      } else {
        const error = await response.json();
        console.error('Failed to create employee:', error.error);
        alert('Failed to create employee: ' + error.error);
      }
    } catch (error) {
      console.error('Error creating employee:', error);
      alert('Error creating employee. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Add New Employee</h3>
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group">
            <label>Employee Name *</label>
            <SearchableDropdown
              options={employeeNames}
              value={formData.name}
              onChange={(value) => setFormData(prev => ({ ...prev, name: value }))}
              placeholder="Search existing employees or type new name..."
              allowCustom={true}
              required={true}
              id="employee-name"
              name="name"
            />
          </div>

          <div className="form-group">
            <label>Team *</label>
            <select
              value={formData.department}
              onChange={handleTeamChange}
              required
            >
              <option value="">Select Team</option>
              {teams.map(team => (
                <option key={team.TeamID} value={team.Title}>{team.Title}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Manager</label>
            <input
              type="text"
              value={formData.manager}
              placeholder="Manager will be auto-populated when team is selected"
              readOnly
              style={{
                backgroundColor: '#f9fafb',
                color: '#6b7280',
                cursor: 'not-allowed'
              }}
            />
          </div>

          <div className="form-group">
            <label>Roles *</label>
            <select
              value=""
              onChange={e => e.target.value && handleRoleAdd(e.target.value)}
              disabled={!formData.department}
            >
              <option value="">
                {formData.department ? 'Select roles to add...' : 'Select Team first'}
              </option>
              {filteredRoles
                .filter(role => !formData.roles.includes(role.Title))
                .map(role => (
                  <option key={role.RoleID} value={role.Title}>{role.Title}</option>
                ))}
            </select>
            <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {formData.roles.map((role, index) => (
                <span
                  key={index}
                  style={{
                    backgroundColor: '#6c5ce7',
                    color: 'white',
                    padding: '4px 12px',
                    borderRadius: '16px',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  {role}
                  <button
                    type="button"
                    onClick={() => handleRoleRemove(role)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'white',
                      cursor: 'pointer',
                      padding: '0',
                      marginLeft: '4px'
                    }}
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn-cancel">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="btn-save">
              {loading ? 'Adding...' : 'Add Employee'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const DeleteConfirmModal = ({ member, isOpen, onClose, onConfirm }) => {
  const [loading, setLoading] = useState(false);

  const handleDelete = async () => {
    setLoading(true);
    
    try {
      const response = await fetch(`/api/employees/${member.id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        onConfirm(member.id);
        onClose();
      } else {
        console.error('Failed to delete employee');
      }
    } catch (error) {
      console.error('Error deleting employee:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content delete-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Delete Employee</h3>
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        
        <div className="modal-form">
          <div className="delete-warning">
            <Trash2 size={48} className="delete-icon" />
            <p>Are you sure you want to delete <strong>{member?.name}</strong>?</p>
            <p className="delete-subtitle">This action cannot be undone.</p>
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn-cancel">
              Cancel
            </button>
            <button 
              type="button" 
              onClick={handleDelete} 
              disabled={loading} 
              className="btn-delete"
            >
              {loading ? 'Deleting...' : 'Delete Employee'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Card for each member
const MemberCard = ({ member }) => (
  <div className="member-card">
    <div className="member-header">
      <div className="member-info">
        <div className="avatar">{member.avatar}</div>
        <div className="member-details">
          <h3>{member.name}</h3>
          <p>{member.email}</p>
          <p>{member.role}</p>
        </div>
      </div>
      <span className={getStatusClass(member.status)}>
        {member.status}
      </span>
    </div>
    <div className="member-metadata">
      <div className="metadata-row" style={{ flex: 1 }}>
        <span className="metadata-label">Team:</span>
        <span>{member.team}</span>
      </div>
      <div className="metadata-row" style={{ flex: 1 }}>
        <span className="metadata-label">Manager:</span>
        <span>{member.manager}</span>
      </div>
      <div className="metadata-row" style={{ flex: 1 }}>
        <span className="metadata-label">Joined:</span>
        <span>{member.joined}</span>
      </div>
    </div>
  </div>
);

const MemberRow = ({ member, onEdit, onDelete }) => (
  <tr>
    <td>
      <div className="list-member-info">
        <div className="avatar-small">{member.avatar}</div>
        <div>
          <div className="list-member-name">{member.name}</div>
          <div className="list-member-email">{member.email}</div>
        </div>
      </div>
    </td>
    <td>
      <span className="editable-field" onClick={() => onEdit(member)} title="Click to edit">
        {member.role}
        <Edit2 size={12} className="edit-icon" />
      </span>
    </td>
    <td>
      <span className="editable-field" onClick={() => onEdit(member)} title="Click to edit">
        {member.team}
        <Edit2 size={12} className="edit-icon" />
      </span>
    </td>
    <td>
      <span className="editable-field" onClick={() => onEdit(member)} title="Click to edit">
        {member.manager}
        <Edit2 size={12} className="edit-icon" />
      </span>
    </td>
    <td>{member.joined}</td>
    <td>
      <span className="editable-field" onClick={() => onEdit(member)} title="Click to edit">
        <span className={getStatusClass(member.status)}>{member.status}</span>
        <Edit2 size={12} className="edit-icon" />
      </span>
    </td>
    <td>
      <button 
        className="delete-btn" 
        onClick={() => onDelete(member)} 
        title="Delete employee"
      >
        <Trash2 size={14} />
      </button>
    </td>
  </tr>
);

// Section for each department under a manager
const DepartmentSection = ({ departmentName, data, viewMode, onEdit, onDelete }) => (
  <div className="department-section">
    <div className="department-header">
      <div className="department-info">
        <div className="department-icon">
          <div className="department-icon-inner"></div>
        </div>
        <span className="department-title">{departmentName}</span>
      </div>
      <span className="member-count">{data.memberCount} members</span>
    </div>
    {viewMode === 'card' ? (
      <div className="member-grid">
        {data.members.map(member => (
          <MemberCard key={member.id} member={member} />
        ))}
      </div>
    ) : (
      <div className="member-list">
        <table className="member-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Role</th>
              <th>Department</th>
              <th>Manager</th>
              <th>Joined</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.members.map(member => (
              <MemberRow key={member.id} member={member} onEdit={onEdit} onDelete={onDelete} />
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const TeamAnalytics = ({ employees }) => {
  const [selectedTeam, setSelectedTeam] = useState(null);

  // Calculate team statistics
  const teamStats = employees.reduce((acc, emp) => {
    const team = emp.department || 'Unassigned';
    if (!acc[team]) {
      acc[team] = { count: 0, members: [] };
    }
    acc[team].count++;
    acc[team].members.push({
      id: emp.id,
      name: emp.name,
      role: emp.role,
      manager: emp.manager,
      status: emp.status || 'Active',
      avatar: emp.avatar || '👤'
    });
    return acc;
  }, {});

  const totalEmployees = employees.length;
  const maxTeamSize = Math.max(...Object.values(teamStats).map(team => team.count));

  const handleTeamClick = (teamName) => {
    setSelectedTeam(selectedTeam === teamName ? null : teamName);
  };

  return (
    <div className="analytics-container">
      <div className="analytics-header">
        <h2>Team Analytics</h2>
        <p>Visual breakdown of team composition and distribution</p>
      </div>
      
      <div className="analytics-grid">
        {/* Summary Cards */}
        <div className="analytics-summary">
          <div className="summary-card">
            <div className="summary-icon">
              <Users size={24} />
            </div>
            <div className="summary-content">
              <h3>{totalEmployees}</h3>
              <p>Total Employees</p>
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-icon">
              <BarChart3 size={24} />
            </div>
            <div className="summary-content">
              <h3>{Object.keys(teamStats).length}</h3>
              <p>Teams</p>
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-icon">
              <Users size={24} />
            </div>
            <div className="summary-content">
              <h3>{Math.round(totalEmployees / Object.keys(teamStats).length)}</h3>
              <p>Avg Team Size</p>
            </div>
          </div>
        </div>

        {/* Team Distribution Chart */}
        <div className="chart-container">
          <h3>Team Distribution</h3>
          <div className="bar-chart">
            {Object.entries(teamStats)
              .sort(([,a], [,b]) => b.count - a.count)
              .map(([teamName, data]) => {
                const percentage = (data.count / totalEmployees) * 100;
                const barHeight = (data.count / maxTeamSize) * 100;
                
                return (
                  <div key={teamName} className="bar-item">
                    <div className="bar-container">
                      <div 
                        className="bar" 
                        style={{ height: `${barHeight}%` }}
                        title={`${teamName}: ${data.count} members (${percentage.toFixed(1)}%)`}
                      >
                        <span className="bar-value">{data.count}</span>
                      </div>
                    </div>
                    <div className="bar-label">{teamName}</div>
                  </div>
                );
              })}
          </div>
        </div>

        {/* Team Details Table */}
        <div className="team-details">
          <h3>Team Breakdown</h3>
          <div className="team-table">
            {Object.entries(teamStats)
              .sort(([,a], [,b]) => b.count - a.count)
              .map(([teamName, data]) => {
                const percentage = (data.count / totalEmployees) * 100;
                const isExpanded = selectedTeam === teamName;
                
                return (
                  <div key={teamName} className="team-section">
                    <div 
                      className={`team-row ${isExpanded ? 'expanded' : ''}`}
                      onClick={() => handleTeamClick(teamName)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="team-name">
                        {teamName}
                        <span className="expand-icon">
                          {isExpanded ? '▼' : '▶'}
                        </span>
                      </div>
                      <div className="team-progress">
                        <div className="progress-bar">
                          <div 
                            className="progress-fill" 
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                        <span className="team-count">{data.count} ({percentage.toFixed(1)}%)</span>
                      </div>
                    </div>
                    
                    {isExpanded && (
                      <div className="team-members">
                        <div className="members-header">
                          <h4>Team Members ({data.count})</h4>
                        </div>
                        <div className="members-list">
                          {data.members.map(member => (
                            <div key={member.id} className="member-item">
                              <div className="member-avatar">{member.avatar}</div>
                              <div className="member-info">
                                <div className="member-name">{member.name}</div>
                                <div className="member-role">{member.role}</div>
                              </div>
                              <div className="member-details">
                                <div className="member-manager">
                                  <span className="detail-label">Manager:</span>
                                  <span className="detail-value">{member.manager}</span>
                                </div>
                                <div className="member-status">
                                  <span className={getStatusClass(member.status)}>
                                    {member.status}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    </div>
  );
};

// Section for each manager
const ManagerSection = ({ managerName, departments, viewMode, onEdit, onDelete }) => (
  <div className="manager-section">
    <div className="manager-header">
      <div className="manager-info">
        <div className="manager-icon">
          <Users className="menu-icon" />
        </div>
        <span className="manager-title">{managerName}</span>
      </div>
      <span className="department-count">
        {Object.keys(departments).length} department{Object.keys(departments).length !== 1 ? 's' : ''}
      </span>
    </div>
    <div className="departments-container">
      {Object.entries(departments).map(([departmentName, data]) => (
        <DepartmentSection
          key={departmentName}
          departmentName={departmentName}
          data={data}
          viewMode={viewMode}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
    </div>
  </div>
);

export default function Dashboard() {
  const { authState } = useOktaAuth();
  const [employees, setEmployees] = useState([]);
  const [page, setPage]           = useState(0);
  const [hasMore, setHasMore]     = useState(true);
  const [initialLoading, setInitialLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedManager, setSelectedManager] = useState('All Managers');
  const [selectedDepartment, setSelectedDepartment] = useState('All Departments');
  const [selectedStatus, setSelectedStatus] = useState('All Status');
  const [viewMode, setViewMode] = useState('card');
  const [currentView, setCurrentView] = useState('dashboard'); // 'dashboard', 'analytics', 'rbac', 'access-requests', 'teams', or 'bulk-upload'
  const [editingMember, setEditingMember] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deletingMember, setDeletingMember] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [pendingResourcesCount, setPendingResourcesCount] = useState(0);
  const [showDebug, setShowDebug] = useState(false);
  const [dropdownOptions, setDropdownOptions] = useState({
    managers: [],
    departments: [],
    statuses: []
  });
  const [teamsData, setTeamsData] = useState([]);
  const [rolesData, setRolesData] = useState([]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const loaderRef = useRef();
  const dashboardContentRef = useRef();

  // Get current user name from Okta auth OR simple auth
  const simpleAuth = sessionStorage.getItem('simpleAuth');
  const isAdminView = sessionStorage.getItem('isAdminView') === 'true';
  const managerInfo = sessionStorage.getItem('managerInfo');
  
  let currentUserName = 'Admin User';
  
  if (simpleAuth && !authState?.isAuthenticated) {
    if (isAdminView) {
      currentUserName = 'Admin User';
    } else if (managerInfo) {
      const parsedInfo = JSON.parse(managerInfo);
      currentUserName = parsedInfo.name || 'Manager';
    }
  } else if (authState?.isAuthenticated) {
    currentUserName = authState.idToken?.claims?.name || 'Admin User';
  }

  useEffect(() => {
    // Fetch dropdown options from the new Teams and Roles tables
    const fetchDropdownOptions = async () => {
      try {
        const [teamsResponse, rolesResponse, oldOptionsResponse] = await Promise.all([
          fetch('/api/teams'),
          fetch('/api/roles'),
          fetch('/api/dropdown-options')
        ]);

        const teams = await teamsResponse.json();
        const roles = await rolesResponse.json();
        const oldOptions = await oldOptionsResponse.json();

        // Extract unique managers from Teams table
        const managers = [...new Set(teams.map(team => team.Manager).filter(Boolean))].sort();
        
        // Extract team titles for departments
        const departments = teams.map(team => team.Title).sort();
        
        // Extract role titles
        const rolesList = roles.map(role => role.Title).sort();

        setDropdownOptions({
          managers: managers.length > 0 ? managers : oldOptions.managers || [],
          departments: departments.length > 0 ? departments : oldOptions.departments || [],
          roles: rolesList.length > 0 ? rolesList : oldOptions.roles || [],
          statuses: oldOptions.statuses || ['Active', 'Inactive', 'Away', 'Onboarding', 'Offboarding']
        });
      } catch (err) {
        console.error('Error fetching dropdown options:', err);
        // Fallback to local data
        const managers = [...new Set(employees.map(emp => emp.manager))].filter(Boolean).sort();
        const departments = [...new Set(employees.map(emp => emp.department))].filter(Boolean).sort();
        const statuses = ['Active', 'Inactive', 'Away', 'Onboarding', 'Offboarding'];
        
        setDropdownOptions({
          managers,
          departments,
          roles: [...new Set(employees.map(emp => emp.role))].filter(Boolean).sort(),
          statuses
        });
      }
    };

    fetchDropdownOptions();
  }, [employees]);

  // Fetch teams and roles data for Quick Stats
  useEffect(() => {
    const fetchTeamsAndRoles = async () => {
      try {
        const [teamsResponse, rolesResponse] = await Promise.all([
          fetch('/api/teams'),
          fetch('/api/roles')
        ]);

        if (teamsResponse.ok && rolesResponse.ok) {
          const teams = await teamsResponse.json();
          const roles = await rolesResponse.json();
          setTeamsData(teams);
          setRolesData(roles);
        }
      } catch (error) {
        console.error('Error fetching teams and roles:', error);
      }
    };

    fetchTeamsAndRoles();
  }, []);

  // Fetch pending resources count
  useEffect(() => {
    const fetchPendingResources = async () => {
      try {
        const response = await fetch('/api/rbac');
        if (response.ok) {
          const rbacEntries = await response.json();
          const pendingCount = rbacEntries.filter(entry => entry.Status === 'Pending').length;
          setPendingResourcesCount(pendingCount);
        }
      } catch (error) {
        console.error('Error fetching pending resources:', error);
      }
    };

    fetchPendingResources();
    
    // Refresh pending count every 30 seconds
    const interval = setInterval(fetchPendingResources, 30000);
    return () => clearInterval(interval);
  }, []);

  // Fetch whenever `page` changes
  useEffect(() => {
    fetch(`/api/employees?limit=${PAGE_SIZE}&offset=${page * PAGE_SIZE}`)
      .then(res => res.json())
      .then(newRows => {
        // Ensure newRows is an array
        if (Array.isArray(newRows)) {
          setEmployees(prev => [...prev, ...newRows]);
          if (newRows.length < PAGE_SIZE) setHasMore(false);
        } else {
          console.error('API response is not an array:', newRows);
          setHasMore(false);
        }
        setInitialLoading(false);
      })
      .catch(err => {
        console.error('Error fetching employees:', err);
        setInitialLoading(false);
        setHasMore(false);
      });
  }, [page]);

  const handleEditMember = (member) => {
    setEditingMember(member);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingMember(null);
  };

  const handleSaveMember = (memberId, updatedData) => {
    setEmployees(prev => prev.map(emp => 
      emp.id === memberId 
        ? { ...emp, role: updatedData.role, manager: updatedData.manager, department: updatedData.department, status: updatedData.status }
        : emp
    ));
  };

  const handleDeleteMember = (member) => {
    setDeletingMember(member);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setDeletingMember(null);
  };

  const handleConfirmDelete = (memberId) => {
    setEmployees(prev => prev.filter(emp => emp.id !== memberId));
  };

  const handleAddEmployee = () => {
    setIsAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
  };

  const handleAddNewEmployee = (newEmployee) => {
    setEmployees(prev => [...prev, newEmployee]);
  };

  useEffect(() => {
    if (!hasMore) return;
    const obs = new IntersectionObserver(
      ([e]) => e.isIntersecting && setPage(p => p + 1),
      { root: null, threshold: 1.0 }
    );
    if (loaderRef.current) obs.observe(loaderRef.current);
    return () => obs.disconnect();
  }, [hasMore]);

  // Filter employees based on search and filters
  const filteredEmployees = employees.filter(emp => {
    const matchesSearch = !searchQuery || 
      emp.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (emp.email && emp.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
      emp.department.toLowerCase().includes(searchQuery.toLowerCase()) ||
      emp.role.toLowerCase().includes(searchQuery.toLowerCase()) ||
      emp.manager.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesManager = selectedManager === 'All Managers' || 
      emp.manager === selectedManager;
    
    const matchesDepartment = selectedDepartment === 'All Departments' || 
      emp.department === selectedDepartment;
    
    const matchesStatus = selectedStatus === 'All Status' || 
      (emp.status || 'Active') === selectedStatus;
    
    return matchesSearch && matchesManager && matchesDepartment && matchesStatus;
  });

  // Group filtered employees by manager first, then by department
  const managerData = filteredEmployees.reduce((acc, emp) => {
    const manager = emp.manager;
    const dept = emp.department;
    
    if (!acc[manager]) {
      acc[manager] = {};
    }
    if (!acc[manager][dept]) {
      acc[manager][dept] = { memberCount: 0, members: [] };
    }
    
    acc[manager][dept].members.push({
      id:      emp.id,
      name:    emp.name,
      email:   emp.email || emp.name.toLowerCase().replace(' ', '.') + '@company.com',
      role:    emp.role,
      team:    dept,
      manager: emp.manager,
      joined:  emp.joined  || '–',
      status:  emp.status  || 'Active',
      avatar:  emp.avatar  || '👤'
    });
    acc[manager][dept].memberCount++;
    return acc;
  }, {});

  // Get unique managers and departments for filter dropdowns
  const uniqueManagers = [...new Set(employees.map(emp => emp.manager))].sort();
  const uniqueDepartments = [...new Set(employees.map(emp => emp.department))].sort();

  // Handle Quick Stats clicks
  const handleTotalEmployeesClick = () => {
    // Scroll to the dashboard content
    if (dashboardContentRef.current) {
      dashboardContentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleRoleManagementClick = () => {
    // Navigate to Role Management (Teams) screen
    setCurrentView('teams');
  };

  // Generate fallback data when debug button is clicked and no data exists
  const generateFallbackData = () => {
    if (employees.length > 0) {
      alert('Data already exists. Clear data first to generate fallback data.');
      return;
    }

    const fallbackEmployees = [
      {
        id: 1,
        name: 'John Smith',
        email: 'john.smith@ryder.com',
        role: 'Senior Developer',
        department: 'Engineering',
        manager: 'Sarah Johnson',
        joined: '2023-01-15',
        status: 'Active',
        avatar: 'J'
      },
      {
        id: 2,
        name: 'Emily Davis',
        email: 'emily.davis@ryder.com',
        role: 'Product Manager',
        department: 'Product',
        manager: 'Michael Brown',
        joined: '2022-11-20',
        status: 'Active',
        avatar: 'E'
      },
      {
        id: 3,
        name: 'Michael Chen',
        email: 'michael.chen@ryder.com',
        role: 'UX Designer',
        department: 'Design',
        manager: 'Sarah Johnson',
        joined: '2023-03-10',
        status: 'Active',
        avatar: 'M'
      },
      {
        id: 4,
        name: 'Sarah Johnson',
        email: 'sarah.johnson@ryder.com',
        role: 'Engineering Manager',
        department: 'Engineering',
        manager: 'David Wilson',
        joined: '2021-06-01',
        status: 'Active',
        avatar: 'S'
      },
      {
        id: 5,
        name: 'David Wilson',
        email: 'david.wilson@ryder.com',
        role: 'VP of Engineering',
        department: 'Engineering',
        manager: 'CEO',
        joined: '2020-01-15',
        status: 'Active',
        avatar: 'D'
      },
      {
        id: 6,
        name: 'Lisa Anderson',
        email: 'lisa.anderson@ryder.com',
        role: 'QA Engineer',
        department: 'Engineering',
        manager: 'Sarah Johnson',
        joined: '2023-02-28',
        status: 'Active',
        avatar: 'L'
      },
      {
        id: 7,
        name: 'Robert Taylor',
        email: 'robert.taylor@ryder.com',
        role: 'DevOps Engineer',
        department: 'Engineering',
        manager: 'Sarah Johnson',
        joined: '2022-09-12',
        status: 'Active',
        avatar: 'R'
      },
      {
        id: 8,
        name: 'Jennifer Martinez',
        email: 'jennifer.martinez@ryder.com',
        role: 'UI Designer',
        department: 'Design',
        manager: 'Sarah Johnson',
        joined: '2023-04-05',
        status: 'Active',
        avatar: 'J'
      },
      {
        id: 9,
        name: 'James Lee',
        email: 'james.lee@ryder.com',
        role: 'Junior Developer',
        department: 'Engineering',
        manager: 'Sarah Johnson',
        joined: '2023-07-01',
        status: 'Onboarding',
        avatar: 'J'
      },
      {
        id: 10,
        name: 'Amanda White',
        email: 'amanda.white@ryder.com',
        role: 'Product Designer',
        department: 'Product',
        manager: 'Michael Brown',
        joined: '2022-12-15',
        status: 'Active',
        avatar: 'A'
      },
      {
        id: 11,
        name: 'Christopher Harris',
        email: 'christopher.harris@ryder.com',
        role: 'Backend Developer',
        department: 'Engineering',
        manager: 'Sarah Johnson',
        joined: '2022-08-20',
        status: 'Active',
        avatar: 'C'
      },
      {
        id: 12,
        name: 'Michelle Thompson',
        email: 'michelle.thompson@ryder.com',
        role: 'Data Analyst',
        department: 'Analytics',
        manager: 'David Wilson',
        joined: '2023-05-10',
        status: 'Active',
        avatar: 'M'
      }
    ];

    setEmployees(fallbackEmployees);
    alert(`Generated ${fallbackEmployees.length} fallback employees for testing!`);
  };

  if (initialLoading) return <div className="loading-container">Loading dashboard...</div>;

  return (
    <div className="dashboard-container">
      {/* Sidebar */}
      <div className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="logo">
          <div className="logo-icon">
            <img src="/ryder.png" alt="Ryder" className="logo-image" />
          </div>
          {!sidebarCollapsed && <span className="logo-text">Ryder</span>}
        </div>
        
        <button 
          className="sidebar-toggle"
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          title={sidebarCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
        >
          {sidebarCollapsed ? <Menu size={20} /> : <ChevronLeft size={20} />}
        </button>

        <div>
          {!sidebarCollapsed && <div className="menu-header">ADMIN MENU</div>}
          <div 
            className={`menu-item ${currentView === 'dashboard' ? 'active' : ''}`}
            onClick={() => setCurrentView('dashboard')}
            style={{ cursor: 'pointer' }}
            title="Admin Dashboard"
          >
            <LayoutDashboard className="menu-icon" />
            {!sidebarCollapsed && <span>Admin Dashboard</span>}
          </div>
          <div 
            className={`menu-item ${currentView === 'teams' ? 'active' : ''}`}
            onClick={() => setCurrentView('teams')}
            style={{ cursor: 'pointer' }}
            title="Team Roles"
          >
            <Users className="menu-icon" />
            {!sidebarCollapsed && <span>Team Roles</span>}
          </div>
          <div 
            className="menu-item" 
            onClick={handleAddEmployee} 
            style={{ cursor: 'pointer' }}
            title="Add Employee"
          >
            <Plus className="menu-icon" />
            {!sidebarCollapsed && <span>Add Employee</span>}
          </div>
          <div 
            className={`menu-item ${currentView === 'rbac' ? 'active' : ''}`}
            onClick={() => setCurrentView('rbac')}
            style={{ cursor: 'pointer' }}
            title="Roles"
          >
            <Settings className="menu-icon" />
            {!sidebarCollapsed && <span>Roles</span>}
          </div>
          <div 
            className={`menu-item ${currentView === 'functional' ? 'active' : ''}`}
            onClick={() => setCurrentView('functional')}
            style={{ cursor: 'pointer' }}
            title="Functional"
          >
            <Users className="menu-icon" />
            {!sidebarCollapsed && <span>Functional</span>}
          </div>
          <div 
            className={`menu-item ${currentView === 'resources' ? 'active' : ''}`}
            onClick={() => setCurrentView('resources')}
            style={{ cursor: 'pointer' }}
            title="List of Resources"
          >
            <Shield className="menu-icon" />
            {!sidebarCollapsed && <span>List of Resources</span>}
          </div>
          <div 
            className={`menu-item ${currentView === 'bulk-upload' ? 'active' : ''}`}
            onClick={() => setCurrentView('bulk-upload')}
            style={{ cursor: 'pointer' }}
            title="Bulk Upload"
          >
            <Download className="menu-icon" />
            {!sidebarCollapsed && <span>Bulk Upload</span>}
          </div>
          <div 
            className={`menu-item ${currentView === 'analytics' ? 'active' : ''}`}
            onClick={() => setCurrentView('analytics')}
            style={{ cursor: 'pointer' }}
            title="Team Analytics"
          >
            <BarChart3 className="menu-icon" />
            {!sidebarCollapsed && <span>Team Analytics</span>}
          </div>
          {/*<div 
            className={`menu-item ${currentView === 'access-requests' ? 'active' : ''}`}
            onClick={() => setCurrentView('access-requests')}
            style={{ cursor: 'pointer' }}
            title="Access Requests"
          >
            <Clock className="menu-icon" />
            {!sidebarCollapsed && <span>Access Requests</span>}
          </div>*/}
        </div>
      </div>

      {/* Main Content */}
      <div className={`main-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        {/* Welcome Header with Combined Message */}
        {currentView === 'dashboard' && (
          <div className="welcome-header-box">
            <div className="welcome-content">
              <div className="welcome-row">
                {pendingResourcesCount > 0 && (
                  <button 
                    className="review-arrow"
                    onClick={() => setCurrentView('rbac')}
                    title={`${pendingResourcesCount} pending resource${pendingResourcesCount !== 1 ? 's' : ''} to review`}
                  >
                    →
                  </button>
                )}
                <div className="welcome-text">
                  <h1>Welcome, {currentUserName}</h1>
                  {pendingResourcesCount > 0 ? (
                    <p className="welcome-message">
                      You have <strong>{pendingResourcesCount} pending resource{pendingResourcesCount !== 1 ? 's' : ''}</strong> to review
                    </p>
                  ) : (
                    <p className="welcome-message">All resources are up to date!</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Dashboard Overview - Quick Stats with Inline Actions */}
        {currentView === 'dashboard' && (
          <div className="overview-box-fullwidth">
            <div className="box-header">
              <h3>Quick Stats</h3>
              <BarChart3 size={20} />
            </div>
            <div className="stats-actions-container">
              <div className="stats-grid-inline">
                <div className="stat-item clickable" onClick={handleTotalEmployeesClick} title="Click to view employee details">
                  <span className="stat-number">{employees.length}</span>
                  <span className="stat-label">Total Employees</span>
                </div>
                <div className="stat-item clickable" onClick={handleRoleManagementClick} title="Click to manage teams">
                  <span className="stat-number">{teamsData.length}</span>
                  <span className="stat-label">Teams</span>
                </div>
                <div className="stat-item clickable" onClick={handleRoleManagementClick} title="Click to view managers">
                  <span className="stat-number">{[...new Set(teamsData.map(team => team.Manager).filter(Boolean))].length}</span>
                  <span className="stat-label">Managers</span>
                </div>
                <div className="stat-item clickable" onClick={handleRoleManagementClick} title="Click to manage roles">
                  <span className="stat-number">{rolesData.length}</span>
                  <span className="stat-label">Total Roles</span>
                </div>
              </div>

              <div className="quick-actions-inline">
                <button className="icon-action-btn" onClick={handleAddEmployee} title="Add Employee">
                  <Plus size={20} />
                  <span className="action-tooltip">Add Employee</span>
                </button>
                <button className="icon-action-btn" onClick={() => setCurrentView('teams')} title="Manage Teams">
                  <Users size={20} />
                  <span className="action-tooltip">Manage Teams</span>
                </button>
                <button className="icon-action-btn" onClick={() => setCurrentView('rbac')} title="Role Management">
                  <Settings size={20} />
                  <span className="action-tooltip">Role Management</span>
                </button>
                <button className="icon-action-btn" onClick={() => window.open('/api/download/csv', '_blank')} title="Export Data">
                  <Download size={20} />
                  <span className="action-tooltip">Export Data</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Main Content Based on Current View */}
        {currentView === 'rbac' ? (
          <RBACManagement />
        ) : currentView === 'access-requests' ? (
          <AccessRequests />
        ) : currentView === 'teams' ? (
          <TeamsManagement />
        ) : currentView === 'functional' ? (
          <FunctionalRoleManagement />
        ) : currentView === 'resources' ? (
          <ResourceManagement />
        ) : currentView === 'bulk-upload' ? (
          <BulkUploadManagement />
        ) : currentView === 'analytics' ? (
          <TeamAnalytics employees={filteredEmployees} />
        ) : (
          <>
        {/* Admin Dashboard Content - Load Directly */}
            <div className="content-box" ref={dashboardContentRef}>
              <div className="box-header">
                <h3>Admin Dashboard</h3>
                <Shield size={20} />
              </div>
              <div className="tab-content">
                <AdminTab />
              </div>
            </div>
          </>
        )}

        {/* Debug Section */}
        <div className="debug-section">
          <button 
            className="debug-toggle"
            onClick={() => setShowDebug(!showDebug)}
            title="Toggle debug information"
          >
            Debug {showDebug ? '▼' : '▶'}
          </button>
          
          {showDebug && (
            <div className="debug-content">
              <div className="debug-info">
                <h4>Debug Information</h4>
                <div className="debug-grid">
                  <div className="debug-item">
                    <span className="debug-label">Current User:</span>
                    <span className="debug-value">{currentUserName}</span>
                  </div>
                  <div className="debug-item">
                    <span className="debug-label">Current View:</span>
                    <span className="debug-value">{currentView}</span>
                  </div>
                  <div className="debug-item">
                    <span className="debug-label">Total Employees:</span>
                    <span className="debug-value">{employees.length}</span>
                  </div>
                  <div className="debug-item">
                    <span className="debug-label">Filtered Employees:</span>
                    <span className="debug-value">{filteredEmployees.length}</span>
                  </div>
                  <div className="debug-item">
                    <span className="debug-label">Pending Resources:</span>
                    <span className="debug-value">{pendingResourcesCount}</span>
                  </div>
                  <div className="debug-item">
                    <span className="debug-label">View Mode:</span>
                    <span className="debug-value">{viewMode}</span>
                  </div>
                  <div className="debug-item">
                    <span className="debug-label">Search Query:</span>
                    <span className="debug-value">{searchQuery || 'None'}</span>
                  </div>
                  <div className="debug-item">
                    <span className="debug-label">Active Filters:</span>
                    <span className="debug-value">
                      {[
                        selectedManager !== 'All Managers' && `Manager: ${selectedManager}`,
                        selectedDepartment !== 'All Departments' && `Dept: ${selectedDepartment}`,
                        selectedStatus !== 'All Status' && `Status: ${selectedStatus}`
                      ].filter(Boolean).join(', ') || 'None'}
                    </span>
                  </div>
                </div>

                {/* Fallback Data Generator */}
                <div className="debug-actions" style={{ 
                  marginTop: '1rem', 
                  paddingTop: '1rem', 
                  borderTop: '1px solid #e5e7eb' 
                }}>
                  <button 
                    onClick={generateFallbackData}
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: employees.length === 0 ? '#CE2028' : '#6b7280',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.5rem',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (employees.length === 0) {
                        e.target.style.background = '#a01a20';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = employees.length === 0 ? '#CE2028' : '#6b7280';
                    }}
                  >
                    {employees.length === 0 ? '🔧 Generate Fallback Data' : '✓ Data Loaded'}
                  </button>
                  {employees.length === 0 && (
                    <p style={{ 
                      margin: '0.5rem 0 0 0', 
                      fontSize: '0.75rem', 
                      color: '#6b7280' 
                    }}>
                      No employee data found. Click to generate test data.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Edit Modal */}
        <EditModal
          member={editingMember}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onSave={handleSaveMember}
          dropdownOptions={dropdownOptions}
        />

        {/* Add Employee Modal */}
        <AddEmployeeModal
          isOpen={isAddModalOpen}
          onClose={handleCloseAddModal}
          onAdd={handleAddNewEmployee}
          dropdownOptions={dropdownOptions}
        />

        {/* Delete Confirmation Modal */}
        <DeleteConfirmModal
          member={deletingMember}
          isOpen={isDeleteModalOpen}
          onClose={handleCloseDeleteModal}
          onConfirm={handleConfirmDelete}
        />
      </div>
    </div>
  );
}
